// https://github.com/tableau/js-api-samples
// https://github.com/tableau-developers/example-tableau-react-embedding
import React, { useContext, useEffect, useRef } from 'react';
import { Viz, VizCreateOptions } from "tableau-js-api/viz";
import { TableauEvent } from 'tableau-js-api/viz.event';
import { FILTER_COHORT_ID } from '../../containers/CohortBuilder/ReportTableau';
import { selectCreateWorkbook, selectEditWorkbook } from "../../containers/Storyboard/storyboardSlice";
import { useAppSelector } from "../../hooks";
import { TABLEAU_API_HOST } from "../../reducers/settings";
import SettingsContext from "../../reducers/SettingsContext";
import { setViz, vizEventListener } from './TableauViz';

// Props are pulled from the Viz class constructor
// https://help.tableau.com/current/api/js_api/en-us/JavaScriptAPI/js_api_ref.htm#viz_class
export interface TableauProps {
    parentElement: HTMLElement
    dashboardURL: string
    ticket: string
    vizOptions?: VizCreateOptions & { [FILTER_COHORT_ID]?: string }
}

// https://help.tableau.com/current/pro/desktop/en-us/embed_filter_add.htm
// https://help.tableau.com/current/api/js_api/en-us/JavaScriptAPI/js_api_sample_filter.htm
// https://help.tableau.com/current/pro/desktop/en-us/embed_list.htm#Object_params
const Tableau: React.FC<TableauProps> = ({ parentElement, dashboardURL, ticket, vizOptions = {} }) => {

    const settingValues = useContext(SettingsContext)
    const tableauHost = settingValues[TABLEAU_API_HOST]

    const editWorkbook = useAppSelector(selectEditWorkbook)
    const createWorkbook = useAppSelector(selectCreateWorkbook)

    // We want to use a ref here to avoid having the viz
    // re-render every single time a parent component re-renders.
    const viz = useRef<Viz | null>(null)
    setViz(viz)

    vizOptions.onFirstInteractive = (event: TableauEvent) => {
        // TODO: use TableauEventName.TAB_SWITCH, FILTER_CHANGE, PARAMETER_VALUE_CHANGE
        event.getViz().addEventListener('tabswitch' as any, vizEventListener)
        event.getViz().addEventListener('filterchange' as any, vizEventListener)
        event.getViz().addEventListener('parametervaluechange' as any, vizEventListener)
    }

    // Make sure we only instantiate the viz once or when options change
    useEffect(() => {
        if (tableauHost) {
            if (createWorkbook) {
                // https://community.tableau.com/s/question/0D54T00000F33NvSAJ/preserve-it-link-in-stale-content-remover-workbook-not-working-in-20194
                // https://server/authoringNewWorkbook/randomString/DataSourceRepositoryURL
                const suffix = '/authoringNewWorkbook/randomString/healthiq-inpatient'
                if (viz.current) {
                    viz.current!.dispose()
                    const newVizURL = `${tableauHost}${suffix}`
                    viz.current = new window.tableau.Viz(parentElement, newVizURL, vizOptions)
                } else {
                    const newVizURL = `${tableauHost}'/trusted/'${ticket}${suffix}`
                    viz.current = new window.tableau.Viz(parentElement, newVizURL, vizOptions)
                }
            } else if (!viz.current) {
                viz.current = new window.tableau.Viz(parentElement, `${tableauHost}/trusted/${ticket}/${dashboardURL}`, vizOptions)
            } else {
                if (editWorkbook) {
                    viz
                        .current
                        .getCurrentUrlAsync()
                        .then(function (currentURL) {
                            viz.current!.dispose()
                            const editVizURL = currentURL.split('?')[0].replace('/views', '/authoring')
                            viz.current = new window.tableau.Viz(parentElement, editVizURL, vizOptions)
                        })
                } else {
                    // if a viz object exists, delete it.
                    viz.current.dispose()
                    const vizURL = `${tableauHost}/${dashboardURL}`;
                    viz.current = new window.tableau.Viz(parentElement, vizURL, vizOptions)
                }
            }
        }
    }, [tableauHost, dashboardURL, createWorkbook, editWorkbook]) // eslint-disable-line react-hooks/exhaustive-deps

    return null
}

export default Tableau